import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  Lock,
  RefreshCw,
  Save,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Unlock,
  User,
  UserCheck,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

interface Permission {
  key: string;
  label: string;
  description: string;
  comingSoon: boolean;
  roles: Record<string, boolean>;
}

interface Role {
  key: string;
  label: string;
}

interface Stats {
  totalPermissions: number;
  totalRoles: number;
  comingSoonCount: number;
}

// Track pending changes
interface PendingChange {
  permissionKey: string;
  roleKey: string;
  isEnabled: boolean;
}

const roleIcons: Record<string, typeof Shield> = {
  hq_admin: ShieldCheck,
  agent: UserCheck,
  affiliate_owner: Building2,
  affiliate_coach: Building2,
  member: User,
  individual: User,
  purchaser: ShoppingBag,
};

const roleColors: Record<string, string> = {
  hq_admin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  agent: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  affiliate_owner: 'bg-green-500/10 text-green-500 border-green-500/20',
  affiliate_coach: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
  member: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  individual: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  purchaser: 'bg-red-500/10 text-red-400 border-red-500/20',
};

export default function AdminPermissions() {
  const adminApi = useAdminApi();
  const { loadPermissions } = usePermissions();
  const adminApiRef = useRef(adminApi);
  adminApiRef.current = adminApi;

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Lock/unlock state
  const [isLocked, setIsLocked] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockInput, setUnlockInput] = useState('');
  const [unlockError, setUnlockError] = useState('');

  // Pending changes tracking (batch save)
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
  const [originalPermissions, setOriginalPermissions] = useState<Permission[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Store original permissions when data loads
  const fetchPermissions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApiRef.current.getPermissions();
      if (response.success && response.data) {
        setPermissions(response.data.permissions);
        setOriginalPermissions(JSON.parse(JSON.stringify(response.data.permissions)));
        setRoles(response.data.roles);
        setStats(response.data.stats);
      } else {
        setError(response.error || 'Failed to load permissions');
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Auto-lock when navigating away (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isLocked && pendingChanges.size > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isLocked, pendingChanges.size]);

  // Handle unlock attempt
  const handleUnlockAttempt = () => {
    if (unlockInput.toLowerCase() === 'unlock') {
      setIsLocked(false);
      setShowUnlockModal(false);
      setUnlockInput('');
      setUnlockError('');
      toast.success('Permission matrix unlocked. Make your changes and click Save.');
    } else {
      setUnlockError('Type "unlock" exactly to proceed');
    }
  };

  // Handle lock (discard changes)
  const handleLock = () => {
    if (pendingChanges.size > 0) {
      if (!confirm('You have unsaved changes. Locking will discard all changes. Continue?')) {
        return;
      }
      // Revert to original permissions
      setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
      setPendingChanges(new Map());
    }
    setIsLocked(true);
    toast.info('Permission matrix locked');
  };

  // Track permission change locally (no API call yet)
  const handlePermissionChange = (permissionKey: string, roleKey: string, isEnabled: boolean) => {
    if (isLocked) return;

    const cellKey = `${permissionKey}-${roleKey}`;

    // Update UI immediately
    setPermissions((prev) =>
      prev.map((p) =>
        p.key === permissionKey ? { ...p, roles: { ...p.roles, [roleKey]: isEnabled } } : p
      )
    );

    // Check if this is reverting to original value
    const originalPermission = originalPermissions.find((p) => p.key === permissionKey);
    const originalValue = originalPermission?.roles[roleKey] ?? false;

    if (isEnabled === originalValue) {
      // Remove from pending changes if reverting to original
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(cellKey);
        return next;
      });
    } else {
      // Add to pending changes
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.set(cellKey, { permissionKey, roleKey, isEnabled });
        return next;
      });
    }
  };

  // Save all pending changes
  const handleSaveChanges = async () => {
    if (pendingChanges.size === 0) {
      toast.info('No changes to save');
      return;
    }

    setIsSaving(true);

    try {
      const changes = Array.from(pendingChanges.values());
      const response = await adminApiRef.current.updatePermissions(
        changes.map((change) => ({
          role: change.roleKey,
          permission_key: change.permissionKey,
          is_enabled: change.isEnabled,
        }))
      );

      if (response.success) {
        toast.success(
          `Saved ${changes.length} permission change${changes.length !== 1 ? 's' : ''}`
        );
        // Update original permissions to match current state
        setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
        setPendingChanges(new Map());
        await loadPermissions();
        // Auto-lock after successful save
        setIsLocked(true);
      } else {
        toast.error(response.error || 'Failed to save permission changes');
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectAllForRole = (roleKey: string, selectAll: boolean) => {
    if (isLocked) return;

    for (const p of permissions.filter((p) => !p.comingSoon && p.roles[roleKey] !== selectAll)) {
      handlePermissionChange(p.key, roleKey, selectAll);
    }
  };

  const handleResetPermissions = async () => {
    if (isLocked) {
      toast.error('Unlock the matrix first to reset permissions');
      return;
    }

    if (
      !confirm(
        'Are you sure you want to reset all permissions to defaults? This will save immediately.'
      )
    ) {
      return;
    }

    setIsResetting(true);
    try {
      const response = await adminApiRef.current.resetPermissions();
      if (response.success) {
        toast.success('Permissions reset to defaults');
        setPendingChanges(new Map());
        await Promise.all([fetchPermissions(), loadPermissions()]);
        setIsLocked(true);
      } else {
        toast.error(response.error || 'Failed to reset permissions');
      }
    } catch (err) {
      toast.error('Failed to reset permissions');
    } finally {
      setIsResetting(false);
    }
  };

  // Count permissions enabled per role
  const getEnabledCount = (roleKey: string) => {
    return permissions.filter((p) => !p.comingSoon && p.roles[roleKey]).length;
  };

  const getActiveCount = () => {
    return permissions.filter((p) => !p.comingSoon).length;
  };

  // Check if a cell has pending changes
  const hasPendingChange = (permissionKey: string, roleKey: string) => {
    return pendingChanges.has(`${permissionKey}-${roleKey}`);
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Permissions Matrix</h1>
        </div>
        <div className="flex gap-2">
          {isLocked ? (
            <Button
              variant="default"
              onClick={() => setShowUnlockModal(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Unlock className="h-4 w-4 mr-2" />
              Unlock to Edit
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                onClick={handleSaveChanges}
                disabled={pendingChanges.size === 0 || isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
                {pendingChanges.size > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {pendingChanges.size}
                  </Badge>
                )}
              </Button>
              <Button variant="outline" onClick={handleLock}>
                <Lock className="h-4 w-4 mr-2" />
                Lock
              </Button>
              <Button
                variant="outline"
                onClick={handleResetPermissions}
                disabled={isResetting || isLoading}
              >
                {isResetting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Reset to Defaults
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Lock Status Banner */}
      {isLocked ? (
        <Card className="mb-6 border-amber-500/50 bg-amber-500/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-500">Permission Matrix is Locked</p>
                <p className="text-sm text-muted-foreground">
                  Click "Unlock to Edit" to make changes. You'll need to confirm your intent.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : pendingChanges.size > 0 ? (
        <Card className="mb-6 border-blue-500/50 bg-blue-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="font-medium text-blue-500">
                    {pendingChanges.size} Unsaved Change{pendingChanges.size !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Click "Save Changes" to apply, or "Lock" to discard.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6 border-green-500/50 bg-green-500/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Unlock className="h-5 w-5 text-green-500" />
              <div>
                <p className="font-medium text-green-500">Permission Matrix Unlocked</p>
                <p className="text-sm text-muted-foreground">
                  Make your changes below. Click "Save Changes" when done, or "Lock" to cancel.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">Permissions</span>
            </div>
            <p className="text-2xl font-bold">{stats?.totalPermissions || 14}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">Roles</span>
            </div>
            <p className="text-2xl font-bold">{stats?.totalRoles || 7}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
            <p className="text-2xl font-bold">
              {(stats?.totalPermissions || 14) - (stats?.comingSoonCount || 2)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-muted-foreground">Coming Soon</span>
            </div>
            <p className="text-2xl font-bold">{stats?.comingSoonCount || 2}</p>
          </CardContent>
        </Card>
      </div>

      {/* Permission Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isLocked ? (
              <Lock className="h-5 w-5 text-amber-500" />
            ) : (
              <Unlock className="h-5 w-5 text-green-500" />
            )}
            Permission Matrix
          </CardTitle>
          <CardDescription>
            {isLocked
              ? 'Matrix is locked. Unlock to make changes.'
              : 'Click checkboxes to grant/revoke access. Changes are saved when you click "Save Changes".'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-500 font-medium">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchPermissions}>
                Try Again
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2">
                    <TableHead className="min-w-[180px] font-bold">Permission</TableHead>
                    {roles.map((role) => {
                      const Icon = roleIcons[role.key] || Shield;
                      const enabledCount = getEnabledCount(role.key);
                      const activeCount = getActiveCount();
                      return (
                        <TableHead key={role.key} className="text-center min-w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <Icon
                              className={`h-5 w-5 ${roleColors[role.key]?.split(' ')[1] || 'text-muted-foreground'}`}
                            />
                            <span className="text-xs font-medium">{role.label}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {enabledCount}/{activeCount}
                            </Badge>
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.map((permission, index) => (
                    <TableRow
                      key={permission.key}
                      className={`animate-fade-in ${permission.comingSoon ? 'opacity-50 bg-muted/20' : ''}`}
                      style={{ animationDelay: `${index * 20}ms` }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{permission.label}</p>
                              {permission.comingSoon && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20"
                                >
                                  <Clock className="h-2.5 w-2.5 mr-1" />
                                  Coming Soon
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {permission.description}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      {roles.map((role) => {
                        const hasAccess = permission.roles[role.key];
                        const isPending = hasPendingChange(permission.key, role.key);
                        const isDisabled =
                          isLocked || permission.comingSoon || role.key === 'purchaser';

                        return (
                          <TableCell key={role.key} className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={hasAccess}
                                disabled={isDisabled}
                                onCheckedChange={(checked: boolean) =>
                                  handlePermissionChange(permission.key, role.key, checked)
                                }
                                className={`
                                  ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}
                                  ${hasAccess ? 'data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500' : ''}
                                  ${isPending ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background' : ''}
                                `}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Quick Actions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Quick Actions by Role
          </CardTitle>
          <CardDescription>
            {isLocked
              ? 'Unlock the matrix to use quick actions'
              : 'Select/deselect all permissions for a role at once'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {roles
              .filter((r) => r.key !== 'purchaser')
              .map((role) => {
                const Icon = roleIcons[role.key] || Shield;
                const enabledCount = getEnabledCount(role.key);
                const activeCount = getActiveCount();
                const allEnabled = enabledCount === activeCount;
                const noneEnabled = enabledCount === 0;

                return (
                  <div
                    key={role.key}
                    className={`p-3 rounded-lg border ${roleColors[role.key] || 'border-border'} ${isLocked ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{role.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {enabledCount}/{activeCount} enabled
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 h-7 text-xs"
                        disabled={isLocked || allEnabled}
                        onClick={() => handleSelectAllForRole(role.key, true)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 h-7 text-xs"
                        disabled={isLocked || noneEnabled}
                        onClick={() => handleSelectAllForRole(role.key, false)}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        None
                      </Button>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-500" />
              Permission Keys
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Badge className="bg-green-500/10 text-green-500">approve_affiliates</Badge>
                <span>Approve/reject affiliate applications</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge className="bg-blue-500/10 text-blue-500">upload_programming</Badge>
                <span>Create and manage workout programming</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge className="bg-purple-500/10 text-purple-500">view_system_metrics</Badge>
                <span>Access platform-wide analytics</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge className="bg-orange-500/10 text-orange-500">manage_users</Badge>
                <span>Add, edit, suspend users</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge className="bg-teal-500/10 text-teal-500">view_commissions</Badge>
                <span>View commission reports</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Coming Soon Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="bg-amber-500/10 text-amber-500 border-amber-500/20"
                >
                  device_linking
                </Badge>
                <span>Link erg devices to accounts</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="bg-amber-500/10 text-amber-500 border-amber-500/20"
                >
                  global_rank
                </Badge>
                <span>View global rankings across affiliates</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Unlock Confirmation Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in border-amber-500/50">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-amber-500/10 p-3">
                    <ShieldAlert className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-500">Security Warning</CardTitle>
                    <CardDescription>Permission matrix modification</CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowUnlockModal(false);
                    setUnlockInput('');
                    setUnlockError('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-500 mb-1">
                      Tampering with permissions can:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      <li>Grant users access to restricted features</li>
                      <li>Remove critical permissions from roles</li>
                      <li>Cause security vulnerabilities</li>
                      <li>Break application functionality</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unlock-confirm">
                  Type <span className="font-mono font-bold text-amber-500">unlock</span> to confirm
                </Label>
                <Input
                  id="unlock-confirm"
                  placeholder="Type 'unlock' to proceed"
                  value={unlockInput}
                  onChange={(e) => {
                    setUnlockInput(e.target.value);
                    setUnlockError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnlockAttempt();
                    }
                  }}
                  className={unlockError ? 'border-red-500' : ''}
                />
                {unlockError && <p className="text-sm text-red-500">{unlockError}</p>}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleUnlockAttempt}
                  disabled={unlockInput.length === 0}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  <Unlock className="h-4 w-4 mr-2" />
                  Unlock Matrix
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUnlockModal(false);
                    setUnlockInput('');
                    setUnlockError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
